import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import OrderService from "services/OrderService";

const initialState = {
  loading: false,
  orders: [],
  order: {},
  total: 0,
  stats: {
    total_orders: 0,
    pending_orders: 0,
    completed_orders: 0,
    cancelled_orders: 0,
    refunded_orders: 0,
    total_revenue: 0,
  },
  message: "",
  showMessage: false,
  filters: {
    dateFilter: null,
    startDate: null,
    endDate: null,
    orderStatus: null,
    paymentStatus: null,
  },
};

export const getOrders = createAsyncThunk(
  "orders/getOrders",
  async ({ page = 1, pagelimit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await OrderService.getOrders(page, pagelimit, filters);
      return {
        orders: response.results?.data || response.results || [],
        total: response.count || 0,
        stats: response.results?.stats || {
          total_orders: response.count || 0,
          pending_orders: 0,
          completed_orders: 0,
          cancelled_orders: 0,
          refunded_orders: 0,
          total_revenue: 0,
        },
        filters,
      };
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching orders");
    }
  }
);
export const getOrder = createAsyncThunk(
  "orders/getOrder",
  async (id, { rejectWithValue }) => {
    try {
      const response = await OrderService.getOrder(id);
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching order");
    }
  }
);

export const updateOrders = createAsyncThunk(
  "orders/updateOrders",
  async (data, { rejectWithValue }) => {
    try {
      const response = await OrderService.updateOrders(data);
      return {
        orders: response.results,
        total: response.count, // Assuming the API returns total count
      };
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching orders");
    }
  }
);

export const deleteOrders = createAsyncThunk(
  "orders/deleteOrders",
  async ({ id, currentPage = 1, pagelimit = 10, filters = {} }, { rejectWithValue, dispatch }) => {
    try {
      const response = await OrderService.deleteOrders({ id });
      dispatch(getOrders({ page: currentPage, pagelimit, filters })); // Refresh orders
      return { response, id };
    } catch (err) {
      return rejectWithValue(err.message || "Error deleting order");
    }
  }
);

export const bulkDeleteOrders = createAsyncThunk(
  "orders/bulkDeleteOrders",
  async (
    { orderIds, currentPage = 1, pagelimit = 10, filters = {} },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await OrderService.bulkSoftDeleteOrders({
        order_ids: orderIds,
      });
      dispatch(getOrders({ page: currentPage, pagelimit, filters }));
      return { response, orderIds };
    } catch (err) {
      return rejectWithValue(err.message || "Error deleting selected orders");
    }
  },
);

// Create the slice for orders
const orderSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    clearMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle getOrders lifecycle
      .addCase(getOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOrders.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.orders = payload.orders;
        state.total = payload.total;
        state.stats = payload.stats || state.stats;
        state.showMessage = false;
      })
      .addCase(getOrders.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(getOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOrder.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.order = payload;
        state.showMessage = false;
      })
      .addCase(getOrder.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(updateOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateOrders.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.orders = payload.orders;
        state.total = payload.total;
        state.showMessage = false;
      })
      .addCase(updateOrders.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(deleteOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteOrders.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (payload?.id !== undefined && payload?.id !== null) {
          state.orders = state.orders.filter((o) => o.order_id !== payload.id);
          state.total = Math.max(0, Number(state.total || 0) - 1);
        }
        state.showMessage = false;
      })
      .addCase(deleteOrders.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(bulkDeleteOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkDeleteOrders.fulfilled, (state, { payload }) => {
        state.loading = false;
        const ids = Array.isArray(payload?.orderIds) ? payload.orderIds : [];
        if (ids.length > 0) {
          state.orders = state.orders.filter((o) => !ids.includes(o.order_id));
          state.total = Math.max(0, Number(state.total || 0) - ids.length);
        }
        state.showMessage = false;
      })
      .addCase(bulkDeleteOrders.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      });
  },
});

// Export actions and reducer
export const { clearMessage } = orderSlice.actions;
export default orderSlice.reducer;
